import { ButtonBase, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@material-ui/core";
import { observer } from "mobx-react";
import { pluralize } from "helpers/pluralize";
import { BatchHistoryEntry } from "models/thorEventModels/BatchHistoryEntry";
import { BatchImpactAssessment } from "models/thorEventModels/BatchImpactAssessment";
import { BatchProductTemperatureRangesImpactStyles } from "./BatchProductTemperatureRangesImpactStyles";
import { useState } from "react";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { services } from "api/serviceConfig";
import { useStore } from "store/StoreConfigs";
import clsx from "clsx";
import BatchOverviewGrid from "components/BatchOverviewGrid/BatchOverviewGrid";
import moment from "moment";
import { BatchRangeImpact } from "models/thorEventModels/BatchRangeImpact";

export interface BatchHistoryProps {
    history: BatchHistoryEntry[];
}

export default observer(function BatchProductTemperatureRangesImpact(batch: BatchImpactAssessment) {
    const { identityStore } = useStore();

    const classes = BatchProductTemperatureRangesImpactStyles();
    const [loadingForm, setLoadingForm] = useState(false);

    const openForm = async () => {
        setLoadingForm(true);

        try {
            // open new window immediately to bypass browser popup blocker
            const newWindow = open("", "_blank");
            newWindow!.document.title = "Loading...";
            newWindow?.focus();

            const response = await services.Thor.getFormDownloadPresignedUrl(
                batch.details.stabilityFormId,
                identityStore.currentCustomerId
            );

            newWindow!.location.href = response.data;
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingForm(false);
        }
    };

    const getFormattedDuration = (data: BatchRangeImpact): string => {
        let result = "";
        const momentDuration = moment.duration(data.durationMinutes, "minutes");
        const days = momentDuration.days();
        const hours = momentDuration.hours();
        const minutes = momentDuration.minutes();
        if (days > 0) {
            result += `${days} ${pluralize("day", days)} `;
        }

        if (hours > 0) {
            result += `${hours} ${pluralize("hour", hours)} `;
        }

        if (minutes > 0) {
            result += `${minutes} ${pluralize("minute", minutes)} `;
        }

        if (result) {
            return result;
        } else {
            return "0 minutes";
        }
    };

    return (
        <>
            <div className={classes.mainGrid}>
                <Typography variant="h5">Cumulative Temperature Excursion Summary</Typography>
                <BatchOverviewGrid
                    gridXS={2}
                    data={[
                        {
                            subTitle: "Date Excursion Observed",
                            item: moment.utc(batch.details.excursionStartedAt).format("DD MMM YYYY HH:mm:ss "),
                        },
                        {
                            subTitle: "Device Quantity",
                            item: batch.details.numberOfDevices,
                        },
                        {
                            subTitle: "",
                            item: "",
                        },
                        {
                            subTitle: "",
                            item: "",
                        },
                        {
                            subTitle: "",
                            item: "",
                        },
                    ]}
                />
            </div>
            <Grid container justifyContent="space-between" className={classes.mainGrid}>
                <Grid item xs={6}>
                    <Table className={classes.table}>
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <TableCell>
                                    <Typography className={classes.tableHeaderCell}>Temperature range</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography className={classes.tableHeaderCell}>
                                        Cumulative excursion time
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className={classes.tableBody}>
                            {batch.batchRangesImpact
                                .slice()
                                .reverse()
                                .map((data, i) => (
                                    <TableRow key={i}>
                                        <TableCell
                                            className={clsx({
                                                [classes.cellBorderBottom]: i === batch.batchRangesImpact.length - 1,
                                            })}
                                        >
                                            <Typography>{`${data.temperatureFrom}°C to  ${data.temperatureTo}°C`}</Typography>
                                        </TableCell>
                                        <TableCell
                                            className={clsx({
                                                [classes.cellBorderBottom]: i === batch.batchRangesImpact.length - 1,
                                            })}
                                        >
                                            <Typography>{getFormattedDuration(data)}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </Grid>

                <Grid item className={classes.specificationGrid} xs={5}>
                    <Grid container spacing={7}>
                        <Grid item>
                            <Typography className={classes.gridTitle}>product stability form #</Typography>
                            {loadingForm ? (
                                <LoadingIndicator className={classes.formLoadingIndicator} />
                            ) : (
                                <ButtonBase className={classes.formButton} onClick={openForm}>
                                    <Typography> {batch.details.stabilityForm}</Typography>
                                </ButtonBase>
                            )}
                        </Grid>
                        <Grid item>
                            <Typography className={classes.gridTitle}>product spec</Typography>
                            <Typography>-</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
});
